/*
 * Timeline rendering rules, ported verbatim from prototypes/mode-a.html so the
 * mockup survives the rewrite untouched. The DOM that uses these classes is
 * built in phase 4; the rules live here from phase 1 to avoid re-deriving the
 * visual language later.
 */

#legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 24px;
  font-size: 12px;
}

#legend .swatch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

#legend .swatch i {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  display: inline-block;
}

#bands {
  display: flex;
  flex-direction: column;
}

.band {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-bottom: 1px solid var(--rule);
  padding-bottom: calc(var(--band-gap) * 1px);
  margin-bottom: calc(var(--band-gap) * 1px);
}

/* On screen, above the chart: the body scrolls sideways and a name pinned to
   its left would scroll away with it. */
.band-label {
  font-weight: bold;
  font-size: 13px;
  line-height: calc(var(--band-label-h) * 1px);
  margin-bottom: 2px;
}

/* On paper, in a side column beside the axis, the way the source spreadsheet
   holds its band names in column 1. Costs 7% of the width and saves a line per
   band, which is the better side of that trade on a chart of sixteen bands. */
.band.gutter {
  position: relative;
  flex-direction: row;
  width: 100%;
}

.band.gutter > .band-label {
  position: absolute;
  left: 0;
  width: calc(var(--gutter-w) * 1px - 8px);
  margin: 0;
  font-size: 10px;
  line-height: 1.15;
  /* Two lines at most, centred on the axis it names. */
  display: flex;
  align-items: center;
  overflow: hidden;
}

.band.gutter > .band-body { margin-left: calc(var(--gutter-w) * 1px); }

.band-body { position: relative; }

.events-row,
.axis-row,
.periods-row { position: relative; }

.tick {
  position: absolute;
  top: 0;
  width: 1px;
  background: var(--rule-soft);
}

.tick.major { background: var(--rule-strong); }

.tick-label {
  position: absolute;
  font-size: 9px;
  /* Explicit, so the label's box is exactly its type size: `normal` varies from
     1.15 to 1.5 depending on the font, and the axis reserves its height by
     calculation — an unpredictable box is what pushed the last row of dates
     onto the first bar. */
  line-height: 1;
  color: var(--ink-faint);
  transform: translateX(-50%);
  white-space: nowrap;
}

.axis-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--rule-strong);
}

.event {
  position: absolute;
  font-size: 10px;
  white-space: nowrap;
  padding-left: 3px;
  border-left: 1px solid var(--rule-strong);
  line-height: 1;
}

/* Near the end of a band the text would leave the chart and stretch the page.
   The marker reads leftwards instead: its tick stays on the event's year, the
   text ends there rather than starting there. */
.event.flip {
  transform: translateX(-100%);
  padding-left: 0;
  padding-right: 3px;
  border-left: none;
  border-right: 1px solid var(--rule-strong);
  text-align: right;
}

.bar {
  position: absolute;
  height: calc((var(--period-lane-h) - var(--bar-gap)) * 1px);
  border-radius: 3px;
  font-size: 10px;
  color: var(--ink-bar);
  padding: 1px 4px;
  box-sizing: border-box;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

/* A bar too narrow for its text lets the label overflow to the right rather
   than truncating it — consistent with labels already being allowed to overlap
   elsewhere (overlapping periods/events). */
.bar.cut-start { border-top-left-radius: 0; border-bottom-left-radius: 0; }
.bar.cut-end { border-top-right-radius: 0; border-bottom-right-radius: 0; }

.bar.cut-start::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.22), transparent);
}

.bar.cut-end::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.22), transparent);
}

/* Vertical guide marking a period's real start/end, running up to the axis
   underneath the stacked bars — useful when lanes sit too low to read exactly
   where a bar begins or ends. */
.date-guide {
  position: absolute;
  top: 0;
  width: 0;
  border-left: 1px dashed var(--rule-soft);
}

.del-btn {
  margin-left: 6px;
  color: var(--danger);
  font-weight: bold;
  cursor: pointer;
  flex: 0 0 auto;
}

.del-btn:hover { text-decoration: underline; }

/* --- Uncertain event dates -------------------------------------------------- */

/* An event whose date is unknown within a range: a bracket under the label,
   deliberately nothing like a period bar. */
.event-span {
  position: absolute;
  height: 3px;
  box-sizing: border-box;
  border-top: 1px solid var(--rule-soft);
  border-left: 1px solid var(--rule-soft);
  border-right: 1px solid var(--rule-soft);
}

/* --- Axis labels ----------------------------------------------------------- */

.tick-label.bold {
  font-weight: bold;
  color: var(--ink);
}

/* --- Selection and pinned lanes (phase 5) ---------------------------------- */

.bar,
.event {
  cursor: pointer;
}

.bar.selected {
  outline: 2px solid var(--rule-strong);
  outline-offset: 1px;
}

.event.selected {
  font-weight: bold;
  border-left-width: 2px;
}

.event.flip.selected {
  border-left-width: 0;
  border-right-width: 2px;
}

/* Same idea for a bar whose label is wider than it is: the overflow runs left,
   so the text ends on the bar instead of starting on it. In a flex row, content
   that does not fit spills past the start edge. */
.bar.label-end {
  justify-content: flex-end;
}

/* An entry whose lane was chosen by hand rather than stacked automatically.
   Drawn with an inset shadow, not a pseudo-element: ::before and ::after are
   already taken by the cut-edge gradients. */
.bar.pinned {
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.3);
}

@media print {
  .bar.selected { outline: none; }
}

/* --- Pagination (implementation-plan.md §5.2) ------------------------------- */

.band.page-break-before {
  break-before: page;
}
